import { Permission } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  ArticleModelName,
  GroupModelName,
  InstitutionModelName,
  InsuranceContractModelName,
  PatientModelName,
  ProductGroupModelName,
  RegionModelName,
  StandardCareProposalModelName,
  TemplateModelName,
  UsersModelName,
} from '../models/model-names';
import { SessionController } from 'src/app/auth/session/session-controller';

import { AccountService } from '../services/account.service';

@Injectable({ providedIn: 'root' })
export class PermissionValidator {
  private _isWeb: boolean;
  constructor(
    private _accountService: AccountService,
    private _session: SessionController,
    private _platform: Platform
  ) {
    this._isWeb = !this._platform.is('hybrid');
  }

  public validate(path: string): Observable<boolean> {
    // no filter on isAuthenticated here: it could never emit for an unauthenticated user and left
    // the guard pending forever; the permission checks below answer false without an account (#26615)
    return from(this._session.ready).pipe(
      switchMap(async _ => {
        if (path.includes('alberta-calendar')) {
          return await this._accountService.userHasPermission('activities', Permission.Read);
        }
        if (path.includes('patient')) {
          return await this._accountService.userHasPermission(PatientModelName, Permission.Read);
        }
        if (path.includes('emergency')) {
          return !this._isWeb && (await this._accountService.userHasPermission('onlineSearch', Permission.Read));
        }
        if (path.includes('insurance-contract')) {
          return (
            this._isWeb && (await this._accountService.userHasPermission(InsuranceContractModelName, Permission.Read))
          );
        }
        if (path.includes('article')) {
          return await this._accountService.userHasPermission(ArticleModelName, Permission.Read);
        }
        if (path.includes('product-group')) {
          return this._isWeb && (await this._accountService.userHasPermission(ProductGroupModelName, Permission.Read));
        }
        if (path.includes('standard-care-proposal')) {
          return await this._accountService.userHasPermission(StandardCareProposalModelName, Permission.Read);
        }
        if (
          path.includes('pharmacy') ||
          path.includes('doctor') ||
          path.includes('hospital') ||
          path.includes('nursing-service') ||
          path.includes('nursing-home') ||
          path.includes('cooperation-partner')
        ) {
          return await this._accountService.userHasPermission(InstitutionModelName, Permission.Read);
        }
        if (path.includes('formulare')) {
          return this._isWeb && (await this._accountService.userHasPermission(TemplateModelName, Permission.Read));
        }
        if (path.includes('calendar')) {
          return await this._accountService.userHasPermission('calendar', Permission.Read);
        }
        if (path.includes('rights-management')) {
          return await this.hasAnyUsermanagementPermission();
        }
        if (path.includes('maintenance')) {
          return await this.userIsItLabs();
        }
        if (path.includes('agent-admin')) {
          return await this._accountService.isAgent();
        }
        if (path.includes('agent-chat')) {
          return this._isWeb && (await this._accountService.isAgent());
        }
        if (path.includes('list/service-task')) {
          return await this._accountService.userHasPermission('serviceTaskList', Permission.Read);
        }

        return true;
      })
    );
  }

  private async hasAnyUsermanagementPermission(): Promise<boolean> {
    return (
      (await this._accountService.userHasPermission(UsersModelName, Permission.Read)) ||
      (await this._accountService.userHasPermission(GroupModelName, Permission.Read)) ||
      (await this._accountService.userHasPermission(RegionModelName, Permission.Read))
    );
  }

  private async userIsItLabs(): Promise<boolean> {
    const tenantId = await this._accountService.getCurrentUserTenantName();
    return tenantId === '1TLabs';
  }
}
